import axios from 'axios';
import { FormConfig, FormValues } from '@topdanmark-onlinesolutions/claim-form-types';
import useAppState from '../state/appState';
import * as logger from '../utils/logger';

/**
 * Fetches the form configuration from the Claim Form API based on the provided form ID.
 *
 * @param {string} formId - The ID of the form to fetch the configuration for.
 * @returns {Promise<FormConfig>} A promise that resolves to the form configuration.
 * @throws Will throw an error if the API request fails.
 */
export const getFormConfigFromApi = async (formId: string): Promise<FormConfig> => {
    const { topConfig } = useAppState.getState();
    const formConfigApi = topConfig?.services.claimFormApi.formConfig;

    if (!formConfigApi || !formConfigApi.url) {
        throw new Error('claimFormApi.formconfig API: url not defined');
    }

    const url = `${formConfigApi.url}${formId}`;

    try {
        const response = await axios.get(url, {
            headers: formConfigApi.headers
        });

        return response.data;
    } catch (error) {
        logger.logError(`Claim Form API at ${url} failed, error=${error}`);
        throw error;
    }
};

/**
 * Fetches pages from the Claim Form API based on the provided form ID and current form values.
 *
 * @param formId - The ID of the form to fetch pages for.
 * @param formValues - The collected form data entered by the user.
 * @returns A promise that resolves to an array of page strings.
 * @throws Will throw an error if the API request fails.
 */
export const getPagesFromApi = async (formId: string, formValues: FormValues): Promise<string[]> => {
    const { topConfig } = useAppState.getState();
    const pagesApi = topConfig?.services.claimFormApi.pages;

    if (!pagesApi || !pagesApi.url) {
        throw new Error('claimFormApi.pages API: url not defined');
    }

    const url = `${pagesApi.url}${formId}`;

    try {
        const response = await axios.post(url, formValues, {
            headers: pagesApi.headers
        });

        return response.data;
    } catch (error) {
        logger.logError(`Claim Form API at ${url} failed, error=${error}`);
        logger.logError(`formValues=${formValues}`);
        throw error;
    }
};
